import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import ReEnergizeHero from '../../components/ReEnergize/ReEnergizeHero';
import ReEnergizeCarousel from '../../components/ReEnergize/ReEnergizeCarousel';
import { images } from '../../components/shared/assets';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { createSelfbookHandler } from '../../utils/selfbook';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { CarouselArrow } from '../../components/shared/CarouselArrow';
import './ReEnergizePage.css';

interface WellnessCard {
  id: string;
  title: string;
  location: string;
  category: string;
  description: string;
  image?: string;
}

const ReEnergizePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const handleBooking = createSelfbookHandler(navigate);
  
  const wellnessCards: WellnessCard[] = [
    {
      id: '1',
      title: t('pages/reEnergize:cards.atacamaRetreat.title'),
      location: t('pages/reEnergize:cards.atacamaRetreat.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.atacamaRetreat.description'),
      image: images.destinationsPage.properties.atacama
    },
    {
      id: '2',
      title: t('pages/reEnergize:cards.santaCatarinaRetreat.title'),
      location: t('pages/reEnergize:cards.santaCatarinaRetreat.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.santaCatarinaRetreat.description'),
      image: images.destinationsPage.properties.patagonia
    },
    {
      id: '3',
      title: t('pages/reEnergize:cards.mendozaSpa.title'),
      location: t('pages/reEnergize:cards.mendozaSpa.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.mendozaSpa.description'),
      image: images.destinationsPage.properties.iguazu
    }
  ];

  // Wellness Treatments Section Component
  const WellnessTreatments: React.FC = () => {
    return (
    <section className="wellness-treatments" data-scroll-section>
      <div className="wellness-treatments__content">
        <h2>{t('pages/reEnergize:treatments.title')}</h2>
        <div className="wellness-treatments__body">
          <p>
            {t('pages/reEnergize:treatments.description')}
          </p>
          <HandDrawnButton
            text={t('pages/reEnergize:treatments.button')}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            onClick={handleBooking}
            className="wellness-treatments__button"
          />
        </div>
      </div>
    </section>
  );
};

  // Horizontal Gallery Carousel for wellness cards
  const WellnessGalleryCarousel: React.FC = () => {
    const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      const cleanup = initializeDragScroll('wellness-gallery-carousel');
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, []);

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 433, behavior: 'smooth' });
    }
  };

  // Extended card data with wellness focus - remove placeholders
  const allCards = [
    ...wellnessCards.map(card => ({ ...card, category: 'wellness' })),
    {
      id: '4',
      title: 'Desert Mindfulness Retreat',
      location: 'awasi atacama',
      category: 'wellness',
      description: 'Experience the profound silence of the Atacama Desert through guided meditation sessions and mindfulness practices that connect you with the natural world.',
      image: images.producingNaturePage.blogCards.image1
    },
    {
      id: '5',
      title: 'Mountain Yoga Sessions',
      location: 'awasi patagonia', 
      category: 'wellness',
      description: 'Begin your day with gentle yoga overlooking the Torres del Paine, breathing in the crisp mountain air while strengthening body and mind.',
      image: images.producingNaturePage.blogCards.image3
    }
  ];

  return (
    <section className="wellness-gallery-carousel" data-scroll-section>
      <div 
        ref={scrollRef}
        className="wellness-gallery-carousel__scroll-container"
        id="wellness-gallery-carousel"
      >
        {allCards.map((card, index) => (
          <div key={card.id} className="wellness-gallery-carousel__card">
            <div className="wellness-gallery-carousel__image">
              {card.image && (
                <img src={card.image} alt={card.title} />
              )}
            </div>
            <div className="wellness-gallery-carousel__content">
              <div className="wellness-gallery-carousel__header">
                <h3>{card.title}</h3>
                <div className="wellness-gallery-carousel__meta">
                  <span>{card.location}</span>
                  <span>-</span>
                  <span>{card.category}</span>
                </div>
              </div>
              <p>{card.description}</p>
              <CTALink 
                text={t('pages/reEnergize:cards.readMore')}
                type="primary"
                color="awasi"
                onClick={() => navigate('/re-energize/treatments')}
                className="wellness-gallery-carousel__link"
              />
            </div>
          </div>
        ))}
      </div>
      <CarouselArrow
        direction="right"
        onClick={handleScrollRight}
        className="wellness-gallery-carousel__arrow"
      />
    </section>
  );
};

  // Seasonal Produce section - Figma Design Implementation
  const SeasonalProduce: React.FC = () => {
    return (
    <section className="seasonal-produce" data-scroll-section>
      {/* Header Section */}
      <div className="seasonal-produce__header">
        <h2>{t('pages/reEnergize:seasonalProduce.title')}</h2>
      </div>
      
      {/* Image with Details Section */}
      <div className="seasonal-produce__image-container">
        <div 
          className="seasonal-produce__background-image"
          style={{ backgroundImage: `url('/assets/857a4182e049ba5243880b0dcb008dfde8ecce1c.png')` }}
        />
        
        {/* Details Box - Positioned overlay */}
        <div className="seasonal-produce__details-box">
          <div className="seasonal-produce__content">
            <h3>{t('pages/reEnergize:seasonalProduce.details.title')}</h3>
            <p>
              {t('pages/reEnergize:seasonalProduce.details.description')}
            </p>
            <CTALink 
              text={t('pages/reEnergize:seasonalProduce.details.button')}
              type="primary"
              color="awasi"
              onClick={() => navigate('/re-energize/nutrition')}
              className="seasonal-produce__link"
            />
          </div>
          
          {/* Page Navigation */}
          <div className="seasonal-produce__pagination">
            {[1, 2, 3, 4].map(pageNum => (
              <PageToggleButton
                key={pageNum}
                number={pageNum}
                isActive={currentPage === pageNum}
                onClick={() => setCurrentPage(pageNum)}
                color="off-white"
                className="seasonal-produce__page-btn"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

  return (
    <div className="re-energize-page">
      {/* Navigation - Transparent with white text/icons for hero */}
      <SmartNavigation 
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
        forceWhiteAssets={true}
      />

      {/* Hero Section */}
      <ReEnergizeHero />

      {/* Wellness Carousel Section */}
      <ReEnergizeCarousel />

      {/* Wellness Treatments Section */}
      <WellnessTreatments />

      {/* Horizontal Gallery Carousel */}
      <WellnessGalleryCarousel />

      {/* Seasonal Produce Section */}
      <SeasonalProduce />

      {/* Instagram Section */}
      <InstagramSection 
        title="Follow us on Instagram @awasiexperience"
        posts={[
          { src: images.film13, alt: "Awasi wellness" },
          { src: images.film14, alt: "Awasi nature" },
          { src: images.film15, alt: "Awasi experience" },
          { src: images.instagram4, alt: "Awasi luxury" }
        ]}
      />
      
      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default ReEnergizePage;